import { useContext, useState } from "react";
import { useDocument } from "../data/useDocument";
import { useProject } from "../data/useProject";
import { UserContext } from "../data/userContext";
import { Project } from "@atgof-common/generated/entities/project";
import { LanguageContext } from "@atgof-common/language";
import { Banner, Button, Text } from "react-native-paper";
import {
  listOfItems,
  PhraseFunction,
  PhraseLookupFunction,
} from "@atgof-common/phrases";
import { View } from "react-native";
import { MultiFactorAuthEnrollmentScreen } from "@firebase-oss/ui-react";
import { FirebaseUIContainer } from "./FirebaseUIContainer";

function providerName(ph: PhraseLookupFunction, providerId: string) {
  if (providerId === "custom") return "Microsoft"; // TODO
  if (["phone", "password"].includes(providerId)) {
    return (ph(providerId) as string).toLocaleLowerCase();
  }
  let name = providerId;
  if (name.endsWith(".com")) name = name.substring(0, name.length - 4);
  if (name.length)
    name = name.charAt(0).toLocaleUpperCase() + name.substring(1);
  return name;
}

export function ProjectAuthenticationPrompt() {
  const { ph } = useContext(LanguageContext);
  const { authUser, idTokenResult, logOut } = useContext(UserContext);
  const [enrolling, setEnrolling] = useState(false);
  const projectRef = useProject();
  const project = useDocument(projectRef);
  if (enrolling) {
    return (
      <FirebaseUIContainer onDismiss={() => setEnrolling(false)}>
        <MultiFactorAuthEnrollmentScreen
          onEnrollment={() => setEnrolling(false)}
        />
      </FirebaseUIContainer>
    );
  }
  if (!project) return null;
  const authMethods: Project["authMethods"] | undefined =
    project.get("authMethods");
  if (!authMethods) return null;
  const projectName: string | undefined = project.get("displayName");
  const providerId = idTokenResult?.signInProvider;
  if (!providerId) return null;
  if (!(providerId in authMethods)) {
    return (
      <Banner visible icon="alert">
        <Text>
          {
            (ph("provider-not-supported-by-project-error") as PhraseFunction)({
              providerName: providerName(ph, providerId),
              projectName,
              supportedProviderList: listOfItems(
                ph("or") as string,
                Object.keys(authMethods)
                  .filter((id) => id !== "custom") // TODO
                  .map((id) => providerName(ph, id)),
              ),
            }) as string
          }
        </Text>
        <Button onPress={logOut}>{ph("log-out") as string}</Button>
      </Banner>
    );
  }
  const supportedFactorIds: string[] =
    authMethods[providerId as keyof Project["authMethods"]];
  const enrolledFactorIds = [
    "no-mfa",
    ...authUser.multiFactor.enrolledFactors.map(({ factorId }) => factorId),
  ];
  const satisfiesMFARequirements = supportedFactorIds?.some((factorId) =>
    enrolledFactorIds.includes(factorId),
  );
  if (!satisfiesMFARequirements) {
    return (
      <Banner visible icon="alert">
        <View style={{ gap: 8 }}>
          <Text>
            {
              (ph("project-requires-mfa") as PhraseFunction)({
                projectName,
              }) as string
            }
          </Text>
          <View style={{ flexDirection: "row" }}>
            <Button mode="contained" onPress={() => setEnrolling(true)}>
              {ph("add") as string}
            </Button>
          </View>
        </View>
      </Banner>
    );
  }
  return null;
}
